import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Sparkles, X, Send, Loader2, MessageSquare } from 'lucide-react';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'sanmiggrc-sanjeev-widget-session';
const TRANSCRIPT_KEY = 'sanmiggrc-sanjeev-widget-transcript';

const generateSessionId = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const readSession = () => {
  try {
    let s = window.sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = generateSessionId();
      window.sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return generateSessionId();
  }
};

const readTranscript = () => {
  try {
    const raw = window.sessionStorage.getItem(TRANSCRIPT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeTranscript = (turns) => {
  try {
    window.sessionStorage.setItem(TRANSCRIPT_KEY, JSON.stringify(turns));
  } catch {
    /* noop */
  }
};

const PRIORITY_BADGE = {
  hot: { label: 'Hot', cls: 'bg-rose-600 text-white' },
  warm: { label: 'Warm', cls: 'bg-amber-500 text-white' },
  cold: { label: '', cls: '' }
};

const PRESETS = [
  'How do I prep for an RBI pre-audit?',
  'DPDP readiness for a fintech?',
  'FRM Guardrail vs FIU-IND VDASP norms?',
  'I4C CIMS 2.0 integration steps?'
];

const AskSanjeevWidget = () => {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [turns, setTurns] = useState(readTranscript);
  const [sessionId] = useState(readSession);
  const scrollRef = useRef(null);

  useEffect(() => {
    writeTranscript(turns);
  }, [turns]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, turns, loading]);

  const ask = useCallback(
    async (q) => {
      const text = (q || '').trim();
      if (!text || loading) return;
      setQuestion('');
      setLoading(true);
      setTurns((prev) => [...prev, { role: 'user', text }]);
      try {
        const res = await axios.post(`${API}/ask-sanjeev`, { question: text, session_id: sessionId });
        setTurns((prev) => [
          ...prev,
          {
            role: 'sanjeev',
            text: res.data.answer,
            priority: res.data.priority,
            usedLLM: res.data.used_llm,
            leadRef: res.data.lead_ref
          }
        ]);
      } catch (err) {
        setTurns((prev) => [
          ...prev,
          { role: 'sanjeev', text: 'Sorry — Sanjeev AI is briefly unreachable. Please try again.', priority: 'cold' }
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading, sessionId]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    ask(question);
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          data-testid="ask-sanjeev-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-[9999] inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-fuchsia-700 to-rose-600 hover:from-fuchsia-800 hover:to-rose-700 text-white shadow-lg shadow-fuchsia-500/30 transition-all hover:scale-[1.02]"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-[13px] font-semibold whitespace-nowrap">Ask Sanjeev AI</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ask-sanjeev-panel"
          className="fixed bottom-4 right-4 z-[9999] w-[min(94vw,400px)] h-[min(80vh,600px)] bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-fuchsia-700 to-rose-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">Ask Sanjeev AI</p>
                <p className="text-[10px] text-white/80 truncate">Sanmiggrc · India regulatory · Claude Sonnet 4.5</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ask-sanjeev-close"
              className="p-1.5 rounded-lg hover:bg-white/15"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Transcript */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-fuchsia-50/40 to-white">
            {turns.length === 0 && !loading && (
              <div className="text-center pt-2">
                <div className="w-12 h-12 mx-auto rounded-full bg-fuchsia-100 flex items-center justify-center mb-3">
                  <Sparkles className="w-5 h-5 text-fuchsia-700" />
                </div>
                <p className="text-sm font-semibold text-gray-900">Hi, I'm Sanjeev.</p>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                  Founder of Sanmiggrc Technologies. Ask me anything on RBI, DPDP, AML, AI Governance, FIU-IND, I4C — I'll point you to the right module and a 3-step playbook.
                </p>
                <div className="mt-4 grid gap-1.5">
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      onClick={() => ask(p)}
                      className="text-left text-[12px] text-gray-700 border border-gray-200 rounded-md px-3 py-2 hover:border-fuchsia-300 hover:bg-fuchsia-50 transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((t, idx) => (
              <ChatBubble key={`${t.role}-${idx}`} turn={t} />
            ))}

            {loading && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Sanjeev is thinking...
              </div>
            )}
          </div>

          {/* Footer / WhatsApp + form */}
          <div className="border-t border-gray-200 p-3 flex-shrink-0 bg-white space-y-2">
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <input
                data-testid="ask-sanjeev-widget-input"
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Type your regulatory question..."
                className="flex-1 h-9 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:border-transparent"
              />
              <button
                type="submit"
                data-testid="ask-sanjeev-widget-send"
                disabled={loading || !question.trim()}
                className="w-9 h-9 rounded-lg bg-fuchsia-700 hover:bg-fuchsia-800 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
            <a
              href="https://wa.me/919000000000?text=Hi%20Sanjeev%20%E2%80%94%20I%E2%80%99d%20like%20to%20talk%20about%20Sanmiggrc."
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-gray-500 hover:text-emerald-700 inline-flex items-center gap-1"
              data-testid="ask-sanjeev-widget-whatsapp"
            >
              <MessageSquare className="w-3 h-3" />
              Or WhatsApp Sanjeev directly
            </a>
          </div>
        </div>
      )}
    </>
  );
};

const ChatBubble = ({ turn }) => {
  const isUser = turn.role === 'user';
  const priorityBadge = PRIORITY_BADGE[turn.priority] || PRIORITY_BADGE.cold;

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-fuchsia-700 text-white rounded-2xl rounded-br-sm px-3.5 py-2 text-sm">
          {turn.text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[88%] bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm">
        <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
          <Sparkles className="w-3 h-3 text-fuchsia-700" />
          <span className="text-[10px] font-semibold text-fuchsia-700 uppercase tracking-wide">Sanjeev</span>
          {priorityBadge.label && (
            <Badge className={`${priorityBadge.cls} text-[9px] py-0 px-1.5`}>{priorityBadge.label}</Badge>
          )}
          {turn.usedLLM && (
            <span className="text-[9px] text-gray-400">Sonnet 4.5</span>
          )}
        </div>
        <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{turn.text}</p>
        {turn.leadRef && (
          <p className="text-[9px] font-mono text-gray-400 mt-1.5">{turn.leadRef}</p>
        )}
      </div>
    </div>
  );
};

export default AskSanjeevWidget;
